import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';

export type OrganizationProfileDraft = {
    organizationId: string;
    formValue: Record<string, unknown>;
    organizationDetail: any;
};

/**
 * Component-scoped Organization header/detail state for the Organization shell
 * and its child tabs. Avoids duplicate getOrganization calls across siblings
 * and preserves Profile drafts across tab navigation.
 */
@Injectable()
export class OrganizationDetailContext {
    private readonly organizationSubject = new BehaviorSubject<any | null>(null);
    private readonly loadErrorSubject = new BehaviorSubject<string | null>(null);
    private loadGeneration = 0;
    private activeOrganizationId: string | null = null;
    private profileDraft: OrganizationProfileDraft | null = null;

    readonly organization$: Observable<any | null> = this.organizationSubject.asObservable();
    readonly loadError$: Observable<string | null> = this.loadErrorSubject.asObservable();

    get organization(): any | null {
        return this.organizationSubject.value;
    }

    get loadError(): string | null {
        return this.loadErrorSubject.value;
    }

    get activeOrganizationIdValue(): string | null {
        return this.activeOrganizationId;
    }

    /**
     * Starts a new load for the given Organization id. Returns a generation token
     * that callers must pass back so stale responses are ignored.
     */
    beginLoad(organizationId: string): number {
        this.loadGeneration += 1;
        this.activeOrganizationId = organizationId;
        this.loadErrorSubject.next(null);
        this.organizationSubject.next(null);
        if (this.profileDraft && this.profileDraft.organizationId !== String(organizationId)) {
            this.profileDraft = null;
        }
        return this.loadGeneration;
    }

    setOrganization(organization: any, generation: number, organizationId: string): void {
        if (generation !== this.loadGeneration) {
            return;
        }
        if (this.activeOrganizationId !== String(organizationId)) {
            return;
        }
        if (organization?.id != null && String(organization.id) !== String(organizationId)) {
            return;
        }
        this.loadErrorSubject.next(null);
        this.organizationSubject.next(organization);
    }

    setLoadError(message: string, generation: number, organizationId: string): void {
        if (generation !== this.loadGeneration) {
            return;
        }
        if (this.activeOrganizationId !== String(organizationId)) {
            return;
        }
        this.organizationSubject.next(null);
        this.loadErrorSubject.next(message || 'Unable to load organization.');
    }

    saveProfileDraft(draft: OrganizationProfileDraft): void {
        if (!draft?.organizationId) {
            return;
        }
        this.profileDraft = {
            organizationId: String(draft.organizationId),
            formValue: {...draft.formValue},
            organizationDetail: draft.organizationDetail,
        };
    }

    getProfileDraft(organizationId: string): OrganizationProfileDraft | null {
        if (!this.profileDraft || this.profileDraft.organizationId !== String(organizationId)) {
            return null;
        }
        return this.profileDraft;
    }

    clearProfileDraft(organizationId?: string): void {
        if (organizationId != null && this.profileDraft?.organizationId !== String(organizationId)) {
            return;
        }
        this.profileDraft = null;
    }

    clear(): void {
        this.loadGeneration += 1;
        this.activeOrganizationId = null;
        this.organizationSubject.next(null);
        this.loadErrorSubject.next(null);
        this.profileDraft = null;
    }
}
